/**
 * trackingImportSlice — Redux state for the tracking CSV import screen
 * (CON-SHIPPS-002).
 *
 * State shape
 * -----------
 * - `csvFile`         — the `File` object selected by the admin.
 * - `previewHeaders`  — column names parsed from the CSV header row.
 * - `previewRows`     — first 10 data rows as string arrays (for the preview table).
 * - `loading`         — true while the multipart POST is in-flight.
 * - `results`         — the full results payload from a successful import.
 * - `error`           — `ApiError` from a failed import (null otherwise).
 *
 * Per `.claude/rules/frontend-data-flow.md` §1, capsules NEVER import
 * this slice; only the view-level container does.
 */
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { submitTrackingImport as apiSubmitTrackingImport } from '@dfq/shared/api/admin/orders';
import { ApiError } from '@dfq/shared/api/_envelope';
import type { TrackingImportResult } from '@dfq/shared/types/trackingImport';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface TrackingImportState {
  csvFile: File | null;
  previewHeaders: string[];
  previewRows: string[][];
  loading: boolean;
  results: TrackingImportResult | null;
  error: ApiError | null;
}

const initialState: TrackingImportState = {
  csvFile: null,
  previewHeaders: [],
  previewRows: [],
  loading: false,
  results: null,
  error: null,
};

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

type ThunkApiError = { rejectValue: ApiError };

export const submitTrackingImportThunk = createAsyncThunk<
  TrackingImportResult,
  File,
  ThunkApiError
>('trackingImport/submit', async (file, { rejectWithValue }) => {
  try {
    return await apiSubmitTrackingImport(file);
  } catch (error) {
    if (error instanceof ApiError) return rejectWithValue(error);
    throw error;
  }
});

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const trackingImportSlice = createSlice({
  name: 'trackingImport',
  initialState,
  reducers: {
    setCsvFile(
      state,
      action: PayloadAction<{
        file: File;
        headers: string[];
        previewRows: string[][];
      }>,
    ) {
      state.csvFile = action.payload.file;
      state.previewHeaders = action.payload.headers;
      state.previewRows = action.payload.previewRows;
      state.results = null;
      state.error = null;
    },
    resetImport(state) {
      state.csvFile = null;
      state.previewHeaders = [];
      state.previewRows = [];
      state.results = null;
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitTrackingImportThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitTrackingImportThunk.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.results = payload;
      })
      .addCase(submitTrackingImportThunk.rejected, (state, { payload, error }) => {
        state.loading = false;
        state.error =
          payload ??
          new ApiError({
            message: error.message ?? 'Import failed',
            status: 0,
          });
      });
  },
});

export const { setCsvFile, resetImport } = trackingImportSlice.actions;
export const trackingImportReducer = trackingImportSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export interface TrackingImportRootStateSlice {
  trackingImport: TrackingImportState;
}

export const selectTrackingImport = (s: TrackingImportRootStateSlice) =>
  s.trackingImport;
